import { useDataChainId } from '@ui/hooks/datachain';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { getContractAddress } from '@ui/services/contract/constants/address';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { useCallback, useMemo } from 'react';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import LVMONRedeemer from '@ui/services/contract/abis/LVMONRedeemer';
import { getLVMONRedeemer } from '@ui/services/contract/contract-instances/getLVMONRedeemer';

export const useStandardClaim = (params?: TxnMutateCallBackParams) => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });
  const { data: walletClient } = useWalletClient({ chainId });
  const { address } = useAccount();

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccess: params?.onSuccess,
    onError: params?.onError,
    onSettled: params?.onSettled,
    onReceiptSettled: params?.onReceiptSettled,
  });

  const { isSimulating, simulate } = useSimulateMutation();

  const write = useCallback(async (index: bigint) => {
    if (!publicClient || !walletClient || !address) {
      console.warn('Wallet not connected');
      return;
    }

    const lvmonRedeemer = getLVMONRedeemer({
      chainId,
      publicClient: publicClient as any,
      walletClient,
    });

    const { isSuccess } = await simulate(async () => {
      await lvmonRedeemer.estimateGas.standardClaim([
        address,
        index,
      ], {
        account: address,
      });
    });

    if (!isSuccess) {
      console.error("Simulation failed");
      return;
    }

    return writeContract({
      abi: LVMONRedeemer,
      address: lvmonRedeemer.address,
      functionName: 'standardClaim',
      args: [address, index],
    });
  }, [chainId, publicClient, walletClient, simulate, writeContract, address]);

  return useMemo(() => {
    return {
      data,
      mutate: write,
      isPending: isSimulating || isPending,
    };
  }, [data, isPending, isSimulating, write]);
};
